/**
 * Checkout prewarm: the popup script (public/pocketsflow-popup.js) loads the
 * checkout in a hidden frame on the seller's page before the buyer clicks, so
 * the popup opens on a checkout that is already there.
 *
 * Two levels:
 * - Warm shell (every seller, automatic): the frame boots our app on
 *   `/checkout/prewarm` (code, fonts, payment-partner script) and waits. On
 *   click the script asks it to open the real checkout path in place (an
 *   in-app navigation: no download, no boot).
 * - Prepared checkout (`pocketsflowPrewarm(options)`): the frame loads the
 *   exact checkout the seller will open, with `prewarm=1`, so the payment
 *   session and the card form are ready before the click. Anything that
 *   counts a visit or a buyer (visitor beacon, ad pixels, analytics,
 *   telemetry abandon) waits for `popup_opened`.
 */

export const PREWARM_PATH = "/checkout/prewarm";
export const PREWARM_QUERY_PARAM = "prewarm";

/** Frame → page: the warm shell booted and can take an open request. */
export const PREWARM_READY_MESSAGE = "pocketsflow:prewarm-ready";
/** Page → frame: open this checkout path in the warm shell. */
export const PREWARM_OPEN_MESSAGE = "pocketsflow:open";
/** Frame → page: a prepared checkout failed to load; open a fresh one. */
export const PREPARE_FAILED_MESSAGE = "pocketsflow:prepare-failed";
/** Page → frame: the popup holding this frame is now visible. */
export const POPUP_OPENED_MESSAGE = "popup_opened";

export function isPrewarmShellPath(pathname: string): boolean {
	return pathname.replace(/\/+$/, "") === PREWARM_PATH;
}

/** A checkout loaded hidden ahead of the click (`?prewarm=1`). */
export function isPreparedCheckout(search: string): boolean {
	try {
		return new URLSearchParams(search).has(PREWARM_QUERY_PARAM);
	} catch {
		return false;
	}
}

// Once shown, a prepared checkout stays shown for the life of the page:
// the checkout remounts on the post-intent URL (which keeps `prewarm`), and
// that remount must not go back to hidden mode.
let preparedShown = false;
export function markPreparedCheckoutShown(): void {
	preparedShown = true;
}
export function preparedCheckoutShown(): boolean {
	return preparedShown;
}

/** Booted hidden: the warm shell or a prepared checkout. */
export function bootsHidden(location: {
	pathname: string;
	search: string;
}): boolean {
	return (
		isPrewarmShellPath(location.pathname) || isPreparedCheckout(location.search)
	);
}

/**
 * The checkout path an open request may navigate the warm shell to: a
 * same-app `/checkout?…` path only (never another route, origin or scheme).
 */
export function checkoutPathFromOpenRequest(data: unknown): string | null {
	if (!data || typeof data !== "object") return null;
	const { type, path } = data as { type?: unknown; path?: unknown };
	if (type !== PREWARM_OPEN_MESSAGE || typeof path !== "string") return null;
	if (path.length > 4096) return null;
	return /^\/checkout(\?|$)/.test(path) ? path : null;
}

/**
 * Origins the checkout talks to right after it opens: our API (product,
 * payment session) and the payment partner's form. The warm shell may have
 * booted minutes before the click, and browsers close idle connections
 * after seconds, so the click would pay DNS + TLS again on the critical path
 * (prod 2026-10-14: the card form took ~1.1s longer in the warm shell).
 */
const CHECKOUT_ORIGINS = [
	"https://js.whop.com",
	"https://whop.com",
	"https://cdn.whop.com",
];

/** Re-opens the checkout's connections; call as the checkout opens. */
export function refreshCheckoutConnections(
	doc: Document = document,
	apiOrigin: string = String(import.meta.env.VITE_APP_API_SERVER_URL ?? ""),
): void {
	const origins = [...CHECKOUT_ORIGINS];
	try {
		if (apiOrigin) origins.unshift(new URL(apiOrigin).origin);
	} catch {
		// No API origin configured: same-origin requests need no preconnect.
	}
	for (const origin of origins) {
		const link = doc.createElement("link");
		link.rel = "preconnect";
		link.href = origin;
		link.crossOrigin = "anonymous";
		doc.head.appendChild(link);
	}
}
